import React, { useMemo } from 'react';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { cn } from '@/lib/utils';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import type { RespondentMap } from './useRespondentMap';

/**
 * TELV1-329 — Render RA markdown CORRECTLY (incl. GFM tables) in both the
 * chat bubble and the artifact panel, with clickable `[Rn]`/`[Qx]`/`[Tn]`
 * citation pills.
 *
 * Architecture: PARSE markdown first (react-markdown + remark-gfm), INJECT
 * pills second. The previous implementation split text on citation tokens
 * BEFORE parsing, which shattered `| … |` table syntax — so GFM tables could
 * never render (chat stripped them; artifacts leaked raw pipes). Parsing
 * first lets remark-gfm build tables/lists/etc., and pills are injected at
 * the React-render level (never serialised to hast), so they bypass
 * rehype-sanitize and stay XSS-safe by construction.
 *
 * XSS: pills are React components; non-pill content flows through
 * react-markdown's default `urlTransform` (rejects `javascript:`/`data:`)
 * + `rehype-sanitize` with the default schema (which already allows GFM
 * table tags + the `align` attribute — no schema extension needed).
 */

/**
 * Match `[R12]` / `[Q<uuid>]` / `[T3]` citation tokens. The `g` flag is
 * required for `String.split`; `i` matches `[Q...]` UUID hex.
 */
const CITATION_PATTERN = /(\[R\d+\])|(\[Q[a-z0-9-]+\])|(\[T\d+\])/gi;

/**
 * Normalise LLM output before markdown parsing. Each rule fixes a recurring
 * model quirk and is independent of the rendering engine:
 *   - strip ```` ```markdown ```` fences the model sometimes wraps output in
 *     (mirrors the shared `<Markdown>` component),
 *   - drop a stray leading `: ` (a `**Label:** ` prefix whose bold tokens
 *     got eaten mid-stream, leaving a dangling colon),
 *   - collapse `[R5]: …` reference-link residue to `[R5] …` so no orphan
 *     colon appears between a pill and the text it introduces.
 */
export function preprocessCitationMarkdown(text: string): string {
  return text
    .replace(/```markdown\n?/g, '')
    .replace(/\n?```$/g, '')
    .replace(/^[ \t]*:\s+/, '')
    .replace(/(\[R\d+\]|\[Q[a-z0-9-]+\]|\[T\d+\]):\s+/gi, '$1 ');
}

interface PillContext {
  respondentMap: RespondentMap;
  openTranscriptPanel: (conversationId: string, messageId?: string, questionId?: string) => void;
}

/**
 * Render a single citation token as the appropriate pill, or `null` if the
 * token isn't one of the three citation patterns.
 *   - `[Rn]` → respondent transcript pill (clickable when the respondent's
 *     conversation_id is known; inert grey pill otherwise),
 *   - `[Q<uuid>]` → `#question-<id>` hash-anchor (green pill),
 *   - `[Tn]` → inert violet theme pill (no navigation target yet).
 */
export function renderCitationPill(
  part: string,
  ctx: PillContext,
  key: React.Key,
): React.ReactNode | null {
  const rMatch = part.match(/^\[R(\d+)\]$/);
  if (rMatch) {
    const respondentIdx = parseInt(rMatch[1], 10);
    const ref = ctx.respondentMap.get(respondentIdx);
    const isClickable = !!ref;
    return (
      <button
        key={key}
        type="button"
        disabled={!isClickable}
        aria-disabled={!isClickable}
        className={cn(
          'inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full transition-opacity align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-1',
          isClickable
            ? 'bg-primary opacity-90 hover:opacity-100 cursor-pointer'
            : 'bg-neutral-300 opacity-60 cursor-not-allowed',
        )}
        aria-label={
          isClickable
            ? `View transcript for Respondent ${rMatch[1]}`
            : `Respondent ${rMatch[1]} (transcript not available in this view)`
        }
        title={
          isClickable ? `View Respondent ${rMatch[1]}'s transcript` : `Respondent ${rMatch[1]}`
        }
        onClick={() => {
          if (ref) ctx.openTranscriptPanel(ref.conversationId, ref.messageId, ref.questionId);
        }}
      >
        {rMatch[1]}
      </button>
    );
  }
  const qMatch = part.match(/^\[Q([a-z0-9-]+)\]$/i);
  if (qMatch) {
    const questionId = qMatch[1];
    return (
      <a
        key={key}
        href={`#question-${questionId}`}
        aria-label={`Jump to question ${questionId}`}
        title={`Question ${questionId}`}
        className="inline-flex items-center justify-center h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-emerald-600 hover:bg-emerald-700 transition-colors align-text-top focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-1 no-underline"
      >
        Q·{questionId.slice(0, 4)}
      </a>
    );
  }
  const tMatch = part.match(/^\[T(\d+)\]$/);
  if (tMatch) {
    return (
      <span
        key={key}
        aria-label={`Theme ${tMatch[1]}`}
        title={`Theme ${tMatch[1]}`}
        className="inline-flex items-center justify-center min-w-5 h-5 px-1.5 mx-0.5 font-semibold text-[11px] text-white rounded-full bg-violet-600 align-text-top"
      >
        T{tMatch[1]}
      </span>
    );
  }
  return null;
}

/**
 * Walk a markdown element's rendered children and replace citation tokens in
 * its string parts with pill components, leaving nested elements (rendered
 * by their own component) untouched. Runs at the React level — pills are
 * never serialised to HTML, so they bypass rehype-sanitize.
 */
export function injectPills(children: React.ReactNode, ctx: PillContext): React.ReactNode {
  return React.Children.map(children, (child) => {
    if (typeof child !== 'string') return child;
    const parts = child.split(CITATION_PATTERN);
    return parts.map((part, i) => {
      if (!part) return null;
      // Content is immutable LLM output and the split is a pure function of
      // it, so positional keys are stable across re-renders.
      // eslint-disable-next-line react/no-array-index-key
      const pill = renderCitationPill(part, ctx, `c-${i}`);
      return pill ?? part;
    });
  });
}

interface CitationPillTextProps {
  /** The markdown text to render (citations interleaved as `[Rn]` tokens). */
  text: string;
  /** Lookup for `[Rn]` pills → conversation/message for the transcript panel. */
  respondentMap: RespondentMap;
  /**
   * Styling hint only (both variants render the SAME full markdown incl.
   * tables): `'inline'` = chat bubble (tighter spacing), `'paragraph'` =
   * artifact body (slightly roomier).
   */
  variant?: 'paragraph' | 'inline';
  /** Optional className applied to the outer wrapper. */
  className?: string;
}

/**
 * Build the react-markdown `components` map. Text-bearing elements run
 * `injectPills` on their string children; structural elements are styled.
 * `variant` only nudges spacing — content/markdown features are identical.
 */
function buildComponents(variant: 'paragraph' | 'inline', ctx: PillContext): Components {
  const pKids = (children: React.ReactNode) => injectPills(children, ctx);
  const pMargin = variant === 'inline' ? 'mb-3 last:mb-0' : 'mb-4 last:mb-0';
  const listMargin = variant === 'inline' ? 'mb-3 last:mb-0' : 'mb-4 last:mb-0';

  return {
    p: ({ node: _node, children, ...props }) => (
      <p className={`${pMargin} leading-relaxed`} {...props}>
        {pKids(children)}
      </p>
    ),
    strong: ({ node: _node, children, ...props }) => (
      <strong className="font-semibold" {...props}>
        {pKids(children)}
      </strong>
    ),
    em: ({ node: _node, children, ...props }) => <em {...props}>{pKids(children)}</em>,
    del: ({ node: _node, children, ...props }) => <del {...props}>{pKids(children)}</del>,
    a: ({ node: _node, children, ...props }) => (
      <a
        className="text-primary hover:underline"
        target="_blank"
        rel="noopener noreferrer"
        {...props}
      >
        {pKids(children)}
      </a>
    ),
    ul: ({ node: _node, children, ...props }) => (
      <ul className={`list-disc pl-5 ${listMargin} leading-relaxed space-y-1`} {...props}>
        {children}
      </ul>
    ),
    ol: ({ node: _node, children, ...props }) => (
      <ol className={`list-decimal pl-5 ${listMargin} leading-relaxed space-y-1`} {...props}>
        {children}
      </ol>
    ),
    li: ({ node: _node, children, ...props }) => (
      <li className="leading-relaxed" {...props}>
        {pKids(children)}
      </li>
    ),
    h1: ({ node: _node, children, ...props }) => (
      <h1 className="text-base font-bold mt-4 mb-2 first:mt-0" {...props}>
        {pKids(children)}
      </h1>
    ),
    h2: ({ node: _node, children, ...props }) => (
      <h2 className="text-sm font-semibold mt-4 mb-1 first:mt-0" {...props}>
        {pKids(children)}
      </h2>
    ),
    h3: ({ node: _node, children, ...props }) => (
      <h3 className="text-sm font-medium mt-3 mb-1 first:mt-0" {...props}>
        {pKids(children)}
      </h3>
    ),
    h4: ({ node: _node, children, ...props }) => (
      <h4 className="text-sm font-medium mt-3 mb-1 first:mt-0" {...props}>
        {pKids(children)}
      </h4>
    ),
    h5: ({ node: _node, children, ...props }) => (
      <h5 className="text-sm font-medium mt-3 mb-1 first:mt-0" {...props}>
        {pKids(children)}
      </h5>
    ),
    h6: ({ node: _node, children, ...props }) => (
      <h6 className="text-sm font-medium mt-3 mb-1 first:mt-0" {...props}>
        {pKids(children)}
      </h6>
    ),
    blockquote: ({ node: _node, children, ...props }) => (
      <blockquote
        className="border-l-2 border-neutral-300 pl-3 my-3 italic text-neutral-600 leading-relaxed"
        {...props}
      >
        {children}
      </blockquote>
    ),
    hr: ({ node: _node, ...props }) => <hr className="my-3 border-neutral-200" {...props} />,
    code: ({ node: _node, className, children, ...props }) => {
      const isBlock = /language-/.test(className || '');
      if (isBlock) {
        return (
          <code className={`${className ?? ''} font-mono text-xs`} {...props}>
            {children}
          </code>
        );
      }
      return (
        <code className="rounded bg-neutral-100 px-1 py-0.5 font-mono text-[0.85em]" {...props}>
          {children}
        </code>
      );
    },
    pre: ({ node: _node, children, ...props }) => (
      <pre
        className="my-3 overflow-x-auto rounded-md bg-neutral-50 border border-neutral-200 p-3 text-xs"
        {...props}
      >
        {children}
      </pre>
    ),
    // GFM tables — wrapped in a horizontal-scroll container so wide tables
    // never break the (narrow) chat bubble or artifact column layout.
    table: ({ node: _node, children, ...props }) => (
      <div className="my-3 overflow-x-auto">
        <table className="w-full text-sm border-collapse" {...props}>
          {children}
        </table>
      </div>
    ),
    thead: ({ node: _node, children, ...props }) => <thead {...props}>{children}</thead>,
    tbody: ({ node: _node, children, ...props }) => <tbody {...props}>{children}</tbody>,
    tr: ({ node: _node, children, ...props }) => (
      <tr className="border-b border-neutral-200" {...props}>
        {children}
      </tr>
    ),
    th: ({ node: _node, children, ...props }) => (
      <th
        className="border border-neutral-200 bg-neutral-50 px-3 py-2 text-left font-semibold align-top"
        {...props}
      >
        {pKids(children)}
      </th>
    ),
    td: ({ node: _node, children, ...props }) => (
      <td className="border border-neutral-200 px-3 py-2 align-top" {...props}>
        {pKids(children)}
      </td>
    ),
  };
}

/**
 * Shared RA markdown renderer with clickable citation pills. Used by the chat
 * bubble (`MessageBubble`) and the artifact renderers that carry `[Rn]`
 * tokens (`ArtifactMarkdownReport`, `ArtifactThemeSummary`).
 */
function CitationPillText({
  text,
  respondentMap,
  variant = 'paragraph',
  className,
}: CitationPillTextProps) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const ctx = useMemo<PillContext>(
    () => ({ respondentMap, openTranscriptPanel }),
    [respondentMap, openTranscriptPanel],
  );
  const components = useMemo(() => buildComponents(variant, ctx), [variant, ctx]);

  if (!text) return null;

  const source = preprocessCitationMarkdown(text);

  return (
    <div className={className}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, defaultSchema]]}
        urlTransform={defaultUrlTransform}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
}

export default CitationPillText;
